import { Box, Grid, Stack } from "@mui/material";
import { FormikProps } from "formik";

import { AttendanceType } from "@/api";
import {
  AttendanceEntryFormValues,
  AttendancePlanFormBase,
  WeeklyPlanningDayFormValues,
  WeeklySeriesDayFormValues,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import { MAX_PRESENT_ENTRIES_PER_DAY } from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.utils";
import { purposeAttendanceType } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { SystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";

import { AttendanceEntryRow } from "../entry-row/AttendanceEntryRow";
import { DayPanelHeading } from "./DayPanelHeading";

interface Props<T extends AttendancePlanFormBase> {
  formik: FormikProps<T>;
  dayIndex: number;
  dayValues: WeeklyPlanningDayFormValues | WeeklySeriesDayFormValues;
  purposeOptions: IOptionItem[];
  purposes: SystemDropdownOptions;
  onAddEntry: (dayIndex: number) => Promise<void>;
  onRemoveEntry: (dayIndex: number, entryIndex: number) => Promise<void>;
  updateDayEntries: (
    dayIndex: number,
    updater: (entries: AttendanceEntryFormValues[]) => AttendanceEntryFormValues[]
  ) => Promise<void>;
  seriesStartDate?: Date | null;
}

export function AttendanceDayPanel<T extends AttendancePlanFormBase>({
  formik,
  dayIndex,
  dayValues,
  purposeOptions,
  purposes,
  onAddEntry,
  onRemoveEntry,
  updateDayEntries,
  seriesStartDate,
}: Props<T>) {
  const { entries } = dayValues;

  const absentCount = entries.filter((e) => e.attendanceType === AttendanceType.Absent).length;
  const presentCount = entries.filter((e) => e.attendanceType === AttendanceType.Present).length;
  const canAddEntry =
    absentCount === 0 && presentCount < MAX_PRESENT_ENTRIES_PER_DAY && entries.every((e) => !!e.purposeKey);

  const effectivePurposeOptions =
    entries.length > 1
      ? purposeOptions.filter(
          (option) => !!option.isHidden || purposeAttendanceType(option.id, purposes) !== AttendanceType.Absent
        )
      : purposeOptions;

  const headingId = `day-panel-heading-${dayIndex}`;

  return (
    <Box role="group" aria-labelledby={headingId}>
      <Grid container spacing={2}>
        <Grid size={3} sx={{ display: "flex", alignItems: "flex-start" }}>
          <DayPanelHeading id={headingId} dayValues={dayValues} seriesStartDate={seriesStartDate} />
        </Grid>
        <Grid size={9}>
          <Stack spacing={3}>
            {entries.map((entry, entryIndex) => (
              <AttendanceEntryRow
                key={entry.key}
                formik={formik}
                dayIndex={dayIndex}
                entryIndex={entryIndex}
                dayValues={dayValues}
                purposeOptions={effectivePurposeOptions}
                purposes={purposes}
                onAdd={entryIndex === 0 ? () => onAddEntry(dayIndex) : undefined}
                canAdd={canAddEntry}
                onRemove={entryIndex > 0 ? () => onRemoveEntry(dayIndex, entryIndex) : undefined}
                updateDayEntries={updateDayEntries}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
